import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { ISalaryState } from "./types";
import { IIDAStore } from "../types";
import { SalaryRecord } from "../../models/salaryRecord";
import { salaryService } from "../../services/Salary.service";
import { SalaryType } from "../../models/salaryType";
import { endOfDay, isSameDay, parseISO, startOfDay } from "date-fns";
import { IPeriode } from "../../utils/dateUtils";
import { StoreCache } from "../storeCache";

export const getInitialState = (): ISalaryState => {
  return {
    salaryRecords: [],
    salaryTypes: [],
    storeCache: new StoreCache()
  };
};

const namespaced = true;

export const getters: GetterTree<ISalaryState, IIDAStore> = {
  GetSalaryTypes: (state) => {
    return state.salaryTypes;
  },
  GetSalaryTypeById: (state) => (salaryTypeId: number) => {
    return state.salaryTypes.find((salaryType: SalaryType) => salaryType.id === salaryTypeId);
  },
  GetSalaryRecordsByEmployeeIdAndDates:
    (state) => (query: { employeeId: number; fromDate: Date; toDate: Date }) => {
      return state.salaryRecords.filter(
        (sr: SalaryRecord) =>
          sr.employee_id === query.employeeId &&
          isSameDay(parseISO(sr.referenced_from_date.toString()), query.fromDate) &&
          isSameDay(parseISO(sr.referenced_to_date.toString()), query.toDate)
      );
    }
};

export const actions: ActionTree<ISalaryState, IIDAStore> = {
  reset({ commit }) {
    commit("reset");
  },
  fetchSalaryRecords(
    { commit, state },
    query: { fromDate: Date; toDate: Date }
  ): Promise<SalaryRecord[]> {
    return new Promise((resolve, reject) => {
      const from: Date = startOfDay(query.fromDate);
      const to: Date = endOfDay(query.toDate);
      const periode = { fromDate: from, toDate: to } as IPeriode;
      if (!state.storeCache.isCached(periode)) {
        try {
          salaryService.fetchSalaryRecords(query.fromDate, query.toDate).then((salaryRecords) => {
            commit("updateSalaryRecords", { salaryRecords });
            return resolve(salaryRecords);
          });
        } catch (error) {
          return reject(error);
        }
      } else {
        resolve(state.salaryRecords);
      }
    });
  },
  fetchSalaryTypes({ commit, state }): Promise<SalaryType[]> {
    return new Promise((resolve, reject) => {
      if (state.salaryTypes.length === 0) {
        try {
          salaryService.fetchSalaryTypes().then((salaryTypes) => {
            commit("updateSalaryTypes", { salaryTypes });
            return resolve(salaryTypes);
          });
        } catch (error) {
          return reject(error);
        }
      } else {
        resolve(state.salaryTypes);
      }
    });
  },
  createSalaryRecord({ commit }, salaryRecord: SalaryRecord): Promise<SalaryRecord> {
    return new Promise((resolve, reject) => {
      salaryService
        .createSalaryRecord(salaryRecord)
        .then((newSalaryRecord) => {
          commit("insertSalaryRecord", newSalaryRecord);
          resolve(newSalaryRecord);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateSalaryRecord({ commit }, salaryRecord: SalaryRecord): Promise<SalaryRecord> {
    return new Promise((resolve, reject) => {
      salaryService
        .updateSalaryRecord(salaryRecord)
        .then((updatedSalaryRecord) => {
          commit("updateSalaryRecord", updatedSalaryRecord);
          resolve(updatedSalaryRecord);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteSalaryRecord({ commit }, salaryRecordId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      salaryService
        .deleteSalaryRecord(salaryRecordId)
        .then((response) => {
          commit("deleteSalaryRecord", salaryRecordId);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  createSalaryType({ commit }, salaryRecordcategory: SalaryType): Promise<SalaryType> {
    return new Promise((resolve, reject) => {
      salaryService
        .createSalaryType(salaryRecordcategory)
        .then((newSalaryType) => {
          commit("insertSalaryType", newSalaryType);
          resolve(newSalaryType);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateSalaryType({ commit }, salaryType: SalaryType): Promise<SalaryType> {
    return new Promise((resolve, reject) => {
      salaryService
        .updateSalaryType(salaryType)
        .then(() => {
          commit("updateSalaryType", salaryType);
          resolve(salaryType);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteSalaryType({ commit }, salaryRecordcategoryId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      salaryService
        .deleteSalaryType(salaryRecordcategoryId)
        .then((response) => {
          commit("deleteSalaryType", salaryRecordcategoryId);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
};

export const mutations: MutationTree<ISalaryState> = {
  reset(state: ISalaryState) {
    Object.assign(state, getInitialState());
  },
  insertSalaryRecord(state: ISalaryState, salaryRecord: SalaryRecord) {
    state.salaryRecords.push(salaryRecord);
  },
  updateSalaryRecord(state: ISalaryState, salaryRecord: SalaryRecord) {
    const index = state.salaryRecords.findIndex((d) => d.id === salaryRecord.id);
    state.salaryRecords.splice(index, 1);
    state.salaryRecords.push(salaryRecord);
  },
  deleteSalaryRecord(state: ISalaryState, salaryRecordId: number) {
    const index = state.salaryRecords.findIndex((d) => d.id === salaryRecordId);
    state.salaryRecords.splice(index, 1);
  },
  updateSalaryRecords(
    state: ISalaryState,
    payload: { salaryRecords: SalaryRecord[]; from: Date; to: Date }
  ) {
    state.salaryRecords = [
      ...state.salaryRecords,
      ...payload.salaryRecords.filter(
        (newSalaryRecords) =>
          !state.salaryRecords.find((existing) => existing.equals(newSalaryRecords))
      )
    ];
  },
  insertSalaryType(state: ISalaryState, salaryType: SalaryType) {
    state.salaryTypes.push(salaryType);
  },
  updateSalaryType(state: ISalaryState, salaryType: SalaryType) {
    const index = state.salaryTypes.findIndex((d) => d.id === salaryType.id);
    state.salaryTypes.splice(index, 1);
    state.salaryTypes.push(salaryType);
  },
  deleteSalaryType(state: ISalaryState, salaryTypeId: number) {
    const index = state.salaryTypes.findIndex((d) => d.id === salaryTypeId);
    state.salaryTypes.splice(index, 1);
  },
  updateSalaryTypes(state: ISalaryState, payload: { salaryTypes: SalaryType[] }) {
    state.salaryTypes = [
      ...state.salaryTypes.filter(
        (oldSalaryTypes) => !payload.salaryTypes.find((newST) => newST.equals(oldSalaryTypes))
      ),
      ...payload.salaryTypes
    ];
  }
};

export const salaryStoreModule: Module<ISalaryState, IIDAStore> = {
  namespaced: namespaced,
  state: getInitialState(),
  getters: getters,
  actions: actions,
  mutations: mutations
};
